import { ApiService } from "../utils/ApiService.js";
import { AppConfig } from "../utils/AppConfig.js";

// Mensaje para mostrar en la pantalla de ingreso después de un ingreso externo fallido
const SS_ERROR_INGRESO = "kb_ingreso_error";

export class AuthService {
  /** true si la instalación usa un proveedor de ingreso externo (ej. LCApii). */
  static isExterno() {
    return AppConfig.auth.provider !== "local";
  }

  /** true si el usuario es editor de alguna categoría (sin ser administrador). */
  static isEditor(user = AuthService.getCurrentUser()) {
    return !!user && user.rol !== "admin" && Array.isArray(user.categoriasEditables) && user.categoriasEditables.length > 0;
  }

  /** true si puede entrar al panel: administrador o editor. El Worker controla cada acción. */
  static puedeGestionar(user = AuthService.getCurrentUser()) {
    return user?.rol === "admin" || AuthService.isEditor(user);
  }

  /**
   * Lleva al usuario a la página de ingreso del proveedor externo. Al terminar, el
   * proveedor vuelve a esta app con el token en el hash (#access=...).
   */
  static irAIngresoExterno() {
    const vuelta = `${window.location.origin}${window.location.pathname}`;
    window.location.href = `${AppConfig.auth.lcapiiUrl}/auth/login.html?redirect=${encodeURIComponent(vuelta)}`;
  }

  /**
   * Si la app se abrió a la vuelta del proveedor externo (#access=<token>&...), canjea ese
   * token por una sesión de KnowFlow. Saca el token de la barra de direcciones antes de
   * cualquier otra cosa. Devuelve true si procesó una vuelta del proveedor.
   */
  static async capturarIngresoExterno() {
    const hash = window.location.hash.slice(1);
    if (!hash.startsWith("access=")) return false;
    const token = new URLSearchParams(hash).get("access");
    history.replaceState(null, "", `${window.location.pathname}#login`);
    try {
      const response = await ApiService.post("auth/externo", { token });
      if (!response?.user?.token) throw new Error(response?.error || "Respuesta inválida");
      localStorage.setItem("currentUser", JSON.stringify(response.user));
      window.location.hash = "dashboard";
    } catch (err) {
      sessionStorage.setItem(SS_ERROR_INGRESO, err.message || "No se pudo ingresar");
    }
    return true;
  }

  /** Error del último ingreso externo (se lee una sola vez). */
  static tomarErrorIngreso() {
    const msg = sessionStorage.getItem(SS_ERROR_INGRESO);
    sessionStorage.removeItem(SS_ERROR_INGRESO);
    return msg;
  }

  /**
   * Login con email y contraseña.
   * La implementación real vs mock la resuelve ApiService internamente.
   */
  static async login(email, password) {
    const response = await ApiService.post("apiapp/login", { email, password });
    const user = response.user;
    if (user) {
      localStorage.setItem("currentUser", JSON.stringify(user));
      return { success: true, user };
    }
    return { success: false, message: response.message || "Credenciales inválidas" };
  }

  static async logout() {
    localStorage.removeItem("currentUser");
    window.location.hash = "login";
  }

  static getCurrentUser() {
    const user = localStorage.getItem("currentUser");
    return user ? JSON.parse(user) : null;
  }

  static async checkAuth() {
    return this.hasValidSession();
  }

  /**
   * Sesión vigente (sincrónico): hay usuario con token y, si es un JWT, no venció.
   * El Router lo consulta en cada navegación: después del logout las rutas
   * protegidas vuelven a pedir login.
   */
  static hasValidSession() {
    const user = this.getCurrentUser();
    if (!user?.token) return false;
    // Solo validar expiración en tokens JWT reales (formato: header.payload.signature)
    const parts = user.token.split(".");
    if (parts.length === 3) {
      try {
        // El payload JWT viene en base64url (- y _ en vez de + y /); atob solo acepta base64
        const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
        if (payload.exp && payload.exp * 1000 < Date.now()) {
          localStorage.removeItem("currentUser");
          return false;
        }
      } catch {
        localStorage.removeItem("currentUser");
        return false;
      }
    }
    return true;
  }
}
